import React from 'react';
import { Box, hexToRgb, Popover, styled, Typography } from '@mui/material';
import { lightGreen } from '@mui/material/colors';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { isEmptyString } from 'lib/typeguards';

const PREFIX = 'MuiGridTextView';

const classes = {
    error: `${PREFIX}-error`
};

const Root = styled('div')(({ theme }) => ({
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    position: 'relative',
    width: '100%',
    [`& .${classes.error}`]: {
        position: 'absolute',
        right: 0,
        bottom: 0,
        lineHeight: 1,
        color: theme.palette.error.main,
        backgroundColor: hexToRgb(lightGreen[100]).replace(')', `, ${0.8})`),
        borderRadius: `${theme.shape.borderRadius} 0`
    }
}));

type IProps = GridRenderCellParams & {
    prompt?: string;
    error?: string;
    children?: React.ReactNode;
};

export const MuiGridRestrictiveCell: React.FC<IProps> = ({
    children,
    prompt,
    error,
    api,
    id,
    field,
    ...props
}) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

    const handleDoubleClick = React.useCallback(
        (e: React.MouseEvent) => {
            const isEditable = api.isCellEditable({ id, field, ...(props as GridRenderCellParams) });
            if (!isEditable && !!prompt && !isEmptyString(prompt)) {
                e.preventDefault();
                e.stopPropagation();
                setAnchorEl(e.currentTarget as HTMLDivElement);
            }
        },
        [api, id, field, props, prompt]
    );

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            const editKeys = ['Enter', 'F2'];
            const isEditable = api.isCellEditable({ id, field, ...(props as GridRenderCellParams) });

            if (editKeys.includes(e.key) && !isEditable && !!prompt && !isEmptyString(prompt)) {
                e.preventDefault();
                e.stopPropagation();
                setAnchorEl(e.currentTarget as HTMLDivElement);
            }
        },
        [api, id, field, props, prompt]
    );

    const handleClose = React.useCallback(() => {
        setAnchorEl(null);
    }, []);

    const open = Boolean(anchorEl);
    const popoverId = open ? `${id}-${field}-popover` : undefined;

    return (
        <>
            <Root tabIndex={0} onDoubleClick={handleDoubleClick} onKeyDown={handleKeyDown}>
                {children}
                {!!error && (
                    <Typography className={classes.error} variant="caption" color="error">
                        {error}
                    </Typography>
                )}
            </Root>
            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption">{prompt}</Typography>
                </Box>
            </Popover>
        </>
    );
};
